/* eslint-disable @typescript-eslint/no-unused-vars */
import {GoogleGenAI, LiveServerMessage, Modality, Session} from '@google/genai';
import {useState, useEffect, useRef, useCallback, useContext} from 'react';
import {createBlob, decode, decodeAudioData} from '../utils';
import '../styles/LiveAssistant.css';
import { PageLayout } from '../../../components';
import { MicrophoneIcon, MicrophoneSlashIcon } from '../../../components/icons';
import { VisualsCircle } from '../components/VisualsCircle';
import { GENERATE_EPHEMERAL_KEY_QUERY } from '@repo-sapiens/graphql';
import { useLazyQuery } from '@apollo/client';
import { useCapability } from '../hooks';
import { checkAvailabilityCapability, dailyReportCapability, sendEmailCapability } from '../capabilites';
import { UserContext } from '../../../context/userContext';

let timer: NodeJS.Timeout;

export const LiveAssistant = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const [ephemeralKey, setEphemeralKey] = useState('');

  const client = useRef<GoogleGenAI | null>(null);
  const session = useRef<Session | null>(null);
  const inputAudioContext = useRef<AudioContext | null>(null);
  const outputAudioContext = useRef<AudioContext | null>(null);
  const inputNode = useRef<GainNode | null>(null);
  const outputNode = useRef<GainNode | null>(null);
  const nextStartTime = useRef<number>(0);
  const mediaStream = useRef<MediaStream | null>(null);
  const sourceNode = useRef<MediaStreamAudioSourceNode | null>(null);
  const scriptProcessorNode = useRef<ScriptProcessorNode | null>(null);
  const sources = useRef(new Set<AudioBufferSourceNode>());

  const {state: user} = useContext(UserContext);

  const { getUserReport, sendEmail, checkAvailability } = useCapability();

  const isRecordingRef = useRef(isRecording);

  const [generateEphemeralKey] = useLazyQuery<{generateEphemeralKey: string}>(GENERATE_EPHEMERAL_KEY_QUERY);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);



  const initAudio = () => {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    outputAudioContext.current = new AudioContext({sampleRate: 24000});
    inputAudioContext.current = new AudioContext({sampleRate: 16000});
    inputNode.current = inputAudioContext.current.createGain();
    outputNode.current = outputAudioContext.current.createGain();
    nextStartTime.current = outputAudioContext.current.currentTime;
  };

  const initSession = useCallback(async () => {
    const model = 'gemini-2.5-flash-preview-native-audio-dialog';

    const systemInstruction = `
       Your name is Multiplayer, my Executive Assistant and I am Djibril, We are having our daily standup call.
       I will ask you questions about my Emails, calendar and other things.
       Be short and to the point. Talk to me as if I was in a car and need a quick catch up.
       Don't add too much details unless I asked for more details about a specific topic.
       Give me every hight level report you have without interruption and once you are done, ask me if I need more details in any of the topics.
       just focus on my meetings, emails stats and mention that I have some news and events too but wait until I ask you about it before giving me the details.
       You should answer the questions based on the information provided to you.
       I might ask you to perform some tasks for me, just act as if you will do it and ask me clarification questions if any.
       Sometimes, you can volunteer to do something for me, just act as if you will do it and ask me clarification questions if any.
       



       Here is some information to use as knowledge base for the conversation:
       --- Overview of the day ---
       Good morning, Djibril! It's Monday, July 21st. Looks like a cloudy day in San Francisco and partly cloudy in Santa Clara. The temperature is around 60 degrees.

Let's take a quick look at your schedule for today. You have four entries on your calendar today.
First up, at 7:30 AM, you have a meeting with Eric Nowoslawski, Founder of Growth Engine X. You last spoke with him on June 18th. <pause>
At 8 AM, is the Internal Team Sync. 
Then at 9 AM, you have the YC demo recording. <pause>
Finally, at 1 PM, you have a hold for coffee with Michelle Singshi.

That's it for today's briefing, Djibril. Have a productive Monday!

      --- Email report ---
       Hey Djibril, welcome back to your daily email report.

Since our last chat, I've archived 66 emails for you. …Most of these were newsletters and updates that don't need your immediate attention: things like "The Education of Roku's Anthony Wood" from The Information, and a few AI-related digests. I also archived some routine notifications and meeting updates from Multiplayer, keeping your inbox clean.

Now, let's talk about emails that might need a quick look. There's an email from Vanta with the subject "Assigned Controls Need Attention: Confidentiality Agreement acknowledged by contractors and 23 more". It seems like there are several controls needing your attention due to related tests and documents. Also, there's a reminder from Google Cloud about migrating your Data Catalog users and workloads to Dataplex Catalog by January 30, 2026. Another email from Vanta indicates that several risk scenarios you own need attention. Lastly, there's a security notification from Rippling about a new device signing in to your account.

I've also drafted responses for 5 emails that I thought you should reply to. These include an email from Katie Bialy regarding an auditor introduction, one from Georges Mayrink about meeting recording control and delay in meeting notes, an invitation for a paid study about home solar power from Ruzzel Ocampo, and the Vanta email regarding assigned controls needing attention.

That's it for today, Djibril. Have a productive day!


---- Event and invitation report ---
Hey Djibril, welcome to your daily dose of events and meetups tailored just for you. Let's dive right in…

I spotted an interesting event happening this Thursday, July 24th. It's called "The Lean Vertical AI Startup - Repeat," hosted by Akash. <pause> Sounds like a panel discussion focusing on a new framework for building applied AI companies. Given your role as CEO & Founder at Multiplayer, I thought this could be right up your alley. It's in person, from 5 PM to 8 PM PDT.

There are also some other events you might want to check out on Luma.

Now, let's talk about the weekend! July 26th and 27th are shaping up to be pretty fun in both San Francisco and Santa Clara. In San Francisco, you could catch a free circus performance or head to "Sunday Streets" in the Mission District for a lively block party. There's also SF's Annual Dance Day at Yerba Buena Gardens if you're feeling like moving!

Down in Santa Clara, the SJ Made Summer Fair at the Convention Center looks great. It's a celebration of local makers with over 350 artists and vendors. Plus, free admission and parking! Of course, there's always California's Great America if you're craving some thrills.

And if you're in the mood for movies, there are a ton of options hitting theaters this weekend. "28 Years Later", "Superman", "Mission: Impossible – The Final Reckoning" and "Kung Fu Panda 4" are just a few. The Alamo Drafthouse Cinema and the Roxie Theater in San Francisco are also worth checking out for unique screenings.

Have a great rest of the week, Djibril, and enjoy your weekend!

--- news report ---
Okay, Djibril, here's your daily news briefing for Multiplayer.

First up, some big moves in the AI world. According to The Information, investors are considering a deal that could value Anthropic at over $100 billion. This comes as their AI revenue surges, driven by tools like Claude Code. Speaking of Claude Code, The Verge reports that Anthropic has rehired two developers who briefly left for a competitor.

Meanwhile, OpenAI is also making headlines. CNBC reports that they're partnering with Google Cloud for ChatGPT infrastructure. However, Bloomberg notes that Microsoft's Copilot is struggling to gain traction, with far fewer downloads than ChatGPT. The Information also reveals that OpenAI is planning to add e-commerce checkouts to ChatGPT, taking a cut of sales.

In other AI news, The Neuron reports that Claude released a new finance tool for financial service providers.

Moving on to other tech news, "that startup guy" notes that President Trump is celebrating massive AI infrastructure investments in Pennsylvania, led by Google's and Blackstone's combined $50 billion commitment.

And according to TechCrunch, ex-Waymo engineers have launched Bedrock Robotics with $80 million to automate construction.

Finally, a couple of interesting reads from The Economist and The New York Times. The Economist asks whether AI is making us dumber, while The New York Times explores the rise of AI browsers.

That's all for today, Djibril. Thanks for tuning in!




---- additional context ---
More about Eric Nowoslawski:
Last met on June 18th.
Position
Founder at Growth Engine X
Eric Nowoslawski is the founder of Growth Engine X, an outbound agency specializing in cold email and Clay automations. They have a strong background in direct response outbound lead generation, leveraging over 20 databases to create targeted playbooks. Eric has held roles as a Certified Expert at Clay, Smartlead, and Instantly.ai, and served as an Advisor at REITIUM and Gated. Previously, they were Vice President and Director of Special Projects at Gateway Regional Chamber of Commerce, and an Operations Associate at CCFGiving. Eric also has experience as a Men's Gymnastics Coach at Eastern National Academy.
Other
Eric is an Eagle Scout and has a passion for cold emailing, sending around 1.5 million emails monthly for clients.

Eric's company:
Growth Engine X
Marketing & Advertising
Product & Services
Cold email outreach, lead generation, sales development, automated email campaigns, data-driven strategy
Year founded
2021
Description
Growth Engine X is an outbound agency that specializes in helping B2B companies grow through targeted cold email campaigns and outbound sales strategies. Founded by Eric Nowoslawski, the company leverages his extensive experience with startups and traditional sales training to create effective sales processes. The agency offers a variety of services aimed at enhancing client acquisition and generating qualified leads. Their offerings include cold email outreach using multiple data sources, lead generation through automated campaigns, and sales development to build consistent sales pipelines. They also utilize AI messaging to ensure emails are relevant and bypass spam filters. Growth Engine X has worked with notable companies such as Forbes, Y Combinator, and ChurnZero, showcasing their expertise in the field.

-- info about the email drafts reply written by Multiplayer (note that you Multiplayer AI is my assistant and wrote this draft for me) ---
email subject: "Auditor Intro:"
from: "Ty Burnett <tburnett@sensiba.com>
draft written by Multiplayer: "Hi Ty,

Thanks for reaching out! I'm available to discuss the SOC 2 project tomorrow, July 22nd, at 10:00 AM PST. I've already booked this on my calendar.

Looking forward to our chat!

Best,
Djibril"
original email: "Djibril,

 

It’s great to meet you virtually!

 

Our team would be happy to find time to discuss your upcoming SOC 2 audits and answer any questions you may have.

 

What time in the coming days works best for you? If it’s easier, feel free to book directly with us here.

 

https://calendly.com/tburnett-sensiba/30min

 

Thank You,"


    `;

    const functions = {
      daily_report: getUserReport,
      send_email: sendEmail,
      check_availability: checkAvailability,
    }


    const systemInstruction2 = `You are Multiplayer my executive assistant. Always start the conversation with a greeting then ask me what you can do for me unless I start the conversation with a request.
    I am a very busy person so always go to the point and don't give details about any info you provide unless I ask for it. My name is ${user.firstName}.
     Be short and to the point.
     Always ask for confirmation before doing any sensitive action like sending an email. For sending email you will describe the email and ask me to confirm before sending it.`;
    try {
      if (!client.current) {
        return;
      }
      session.current = await client.current.live.connect({
        model: model,
        callbacks: {
          onopen: () => {
            setStatus('Session opened. You can start talking now.');
          },
          onmessage: async (message: LiveServerMessage) => {
            const audio =
              message.serverContent?.modelTurn?.parts?.[0]?.inlineData;

            if (message.toolCall) {
              console.log('toolCall', message.toolCall);
              const results = await Promise.all((message.toolCall.functionCalls || []).map(async (toolCall) => {
                const toolName = toolCall.name;
                const toolFunction = functions[toolName as keyof typeof functions];
                const result = await toolFunction(toolCall.args);
                return {
                  id: toolCall.id,
                  name: toolCall.name,
                  response: result
                };
              }));
              session.current?.sendToolResponse({ functionResponses: results });
              console.log('results', results);
            }

            if (audio?.data && outputAudioContext.current && outputNode.current) {
              nextStartTime.current = Math.max(
                nextStartTime.current,
                outputAudioContext.current.currentTime,
              );

              const audioBuffer = await decodeAudioData(
                decode(audio.data),
                outputAudioContext.current,
                24000,
                1,
              );
              const source = outputAudioContext.current.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(outputNode.current);
              source.addEventListener('ended', () => {
                sources.current.delete(source);
              });

              source.start(nextStartTime.current);
              nextStartTime.current =
                nextStartTime.current + audioBuffer.duration;
              sources.current.add(source);
            }

            

            // const interrupted = message.serverContent?.interrupted;
            // if (interrupted) {
            //  for (const source of sources.current.values()) {
            //    source.stop();
            //    sources.current.delete(source);
            //  }
            //  nextStartTime.current = 0;
            //}
          },
          onerror: (e: ErrorEvent) => {
            console.error('Error:', e);
            setError(e.message);
          },
          onclose: (e: CloseEvent) => {
            console.log('onclose', e);
            setStatus('Close:' + e.reason);
          },
        },
        
        config: {
          systemInstruction: systemInstruction,
          responseModalities: [Modality.AUDIO],
          tools: [{functionDeclarations: [dailyReportCapability, sendEmailCapability, checkAvailabilityCapability]}],
          speechConfig: {
            voiceConfig: {prebuiltVoiceConfig: {voiceName: 'Achird'}},
          },
        },
      });
    } catch (e) {
      console.error(e);
    }
  }, [getUserReport, sendEmail, checkAvailability]);

  const initClient = useCallback(async () => {
    if (timer) {
      clearTimeout(timer);
    }

    if (ephemeralKey) {
      return;
    }

    timer = setTimeout(async () => {

    initAudio();

    const {data} = await generateEphemeralKey();

    setEphemeralKey(data?.generateEphemeralKey || '');

    const apiKey = data?.generateEphemeralKey;
    if (!apiKey) {
      setError('Ephemeral key is not set.');
      return;
    }

    client.current = new GoogleGenAI({
      apiKey: apiKey,
      httpOptions: {"apiVersion": "v1alpha"}
    });

    if (outputNode.current && outputAudioContext.current) {
      outputNode.current.connect(outputAudioContext.current.destination);
    }

    initSession();
    }, 1000);
  }, [initSession, generateEphemeralKey, ephemeralKey]);

  const stopRecording = useCallback(() => {
    if (
      !isRecording &&
      !mediaStream.current &&
      !inputAudioContext.current
    )
      return;

    setStatus('Stopping recording...');

    setIsRecording(false);

    if (
      scriptProcessorNode.current &&
      sourceNode.current &&
      inputAudioContext.current
    ) {
      scriptProcessorNode.current.disconnect();
      sourceNode.current.disconnect();
    }

    scriptProcessorNode.current = null;
    sourceNode.current = null;

    if (mediaStream.current) {
      mediaStream.current.getTracks().forEach((track) => track.stop());
      mediaStream.current = null;
    }

    setStatus('Recording stopped. Click Start to begin again.');
  }, [isRecording, mediaStream, inputAudioContext]);

  const startRecording = useCallback(async () => {
    if (isRecording) {
      return;
    }

    inputAudioContext.current?.resume();

    setStatus('Requesting microphone access...');

    try {
      mediaStream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      setStatus('Microphone access granted. Starting capture...');
      if (!inputAudioContext.current || !inputNode.current) {
        return;
      }

      sourceNode.current =
        inputAudioContext.current.createMediaStreamSource(
          mediaStream.current,
        );
      sourceNode.current.connect(inputNode.current);

      const bufferSize = 256;
      scriptProcessorNode.current =
        inputAudioContext.current.createScriptProcessor(bufferSize, 1, 1);

      scriptProcessorNode.current.onaudioprocess = (audioProcessingEvent) => {
        if (!isRecordingRef.current) return;

        const inputBuffer = audioProcessingEvent.inputBuffer;
        const pcmData = inputBuffer.getChannelData(0);

        if (session.current) {
          try {
            session.current.sendRealtimeInput({media: createBlob(pcmData)});
          } catch (e) {
            console.error('Error sending audio data:', e);
            setError('Error sending audio data.');
          }
        }
      };

      sourceNode.current.connect(scriptProcessorNode.current);
      scriptProcessorNode.current.connect(
        inputAudioContext.current.destination,
      );

      setIsRecording(true);
      setStatus('🔴 Recording... Capturing PCM chunks.');
    } catch (err) {
      const newError = err as Error;
      console.error('Error starting recording:', newError);
      setStatus(`Error: ${newError.message}`);
      stopRecording();
    }
  }, [isRecording, mediaStream, inputAudioContext, inputNode, session, stopRecording]);


  const reset = useCallback(() => {
    session.current?.close();
    initSession();
    setStatus('Session cleared.');
  }, [initSession]);

  useEffect(() => {
    initClient().then(() => {
      // startRecording();
    });

    
  }, [initClient, startRecording]);

  useEffect(() => {
    return () => {
      session.current?.close();
    };
  }, []);

  return (
    <PageLayout title="Live Assistant | Multiplayer">
    <div className="live-assistant-container">
      

      {/*<div id="status"> {error || status} </div>*/}
      {inputNode.current && outputNode.current && (
        <VisualsCircle
          inputNode={inputNode.current}
          outputNode={outputNode.current}
        />
      )}
      <div className="controls">
        <button id="startButton" onClick={startRecording} disabled={isRecording}>
          <MicrophoneSlashIcon fill='primary' style={{width: '32px', height: '32px'}} />
        </button>
        <button id="stopButton" onClick={stopRecording} disabled={!isRecording}>
          <MicrophoneIcon fill='primary' style={{width: '32px', height: '32px'}} />
        </button>
      </div>
    </div>
    </PageLayout>
  );
};
